/**
 * Toast Notification Component
 * Professional replacement for browser's alert()
 */

import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useEffect } from 'react'

export default function Toast({ 
  isOpen, 
  onClose, 
  message, 
  type = 'success', // 'success', 'error', 'warning', 'info'
  duration = 5000,
  position = 'top-right' // 'top-right', 'top-center', 'bottom-right', 'bottom-center'
}) {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [isOpen, duration, onClose])

  if (!isOpen) return null

  const types = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
    },
    error: {
      icon: XCircle,
      iconColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
    }
  }

  const positions = {
    'top-right': 'top-4 right-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
  }

  const config = types[type]
  const Icon = config.icon

  return (
    <div className={`fixed ${positions[position]} z-50 animate-slideDown`}>
      <div className={`${config.bgColor} border rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px] max-w-md`}>
        <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} />
        <p className="text-sm font-medium text-gray-900 dark:text-white flex-1">
          {message}
        </p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
